import { computed, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { NgxDateRangePickerOutput } from 'ngx-date-range-picker';
import * as Selectors from '../../store/selectors/com.selectors';
import { DaterangeISO, DSTStatus, SelectedDateRange } from '../models/daterange';
import { DataViewModelService } from './data-vm.service';

@Injectable({ providedIn: 'root' })
export class DateService {
  private currentRegViewConfigSignal = toSignal(this.store.select(Selectors.selectCurrentRegViewConfig), { initialValue: null });

  constructor(
    private dataVMService: DataViewModelService,
    private store: Store
  ) {}

  public dstStatusSignal: Signal<DSTStatus> = computed(() => {
    const regViewConfig = this.currentRegViewConfigSignal();
    const currentSelectedDate = this.dataVMService.selectedFromDayPickerFormControl.value?.start;

    if (!regViewConfig?.timeZone || !currentSelectedDate) {
      return {
        hasDSTTransition: false,
        type: null
      };
    }

    const timezone = regViewConfig.timeZone;

    // Extract the date string and re-create it in the target timezone
    const dateString = currentSelectedDate.format('YYYY-MM-DD');
    const startOfDay = moment.tz(dateString, timezone).startOf('day');
    const endOfDay = startOfDay.clone().endOf('day');

    const startIsDST = startOfDay.isDST();
    const endIsDST = endOfDay.isDST();

    if (startIsDST === endIsDST) {
      return { hasDSTTransition: false, type: null };
    }

    return {
      hasDSTTransition: true,
      type: !startIsDST && endIsDST ? 'spring_forward' : 'fall_back'
    };
  });

  getISODate(dateRange: NgxDateRangePickerOutput, timezone: string): DaterangeISO {
    const formattedStartDate = moment.tz(dateRange.start?.format('YYYY-MM-DD HH:mm:ss'), timezone);
    const formattedEndDate = moment.tz(dateRange.end?.format('YYYY-MM-DD HH:mm:ss'), timezone);

    const startDate = formattedStartDate.startOf('day');
    const endDate = formattedEndDate.endOf('day');

    const startOfWeek = startDate.clone().startOf('week').toISOString();
    const endOfWeek = endDate.clone().endOf('week').toISOString();

    return {
      start: startOfWeek,
      end: endOfWeek
    };
  }

  getDaysBetweenDates(dateRange: SelectedDateRange) {
    const days: number[] = [];
    for (let m = dateRange.start; m.isBefore(dateRange.end); m.add(1, 'days')) {
      days.push(m.date());
    }
    return days;
  }
}
